import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { 
  FiMail, FiEdit2, FiEye, FiRefreshCw, FiSave, FiType,
  FiAlertCircle, FiChevronDown, FiBold, FiItalic, FiUnderline,
  FiList, FiAlignLeft, FiAlignCenter, FiLink
} from 'react-icons/fi';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Company info for header/footer
const COMPANY_INFO = {
  name: "Just A Smile Events",
  logo: "https://2026.justasmile-events.com/wp-content/uploads/2023/04/JUST-A-SMILE-EVENTS-LOGO-new-2025-sans-fond-1-1024x1024.png",
  address: "23 Avenue de la Crau, 13118 ENTRESSEN",
  phone: "06 74 72 95 12",
  email: "justasmile.events@gmail.com",
  website: "www.justasmile-events.com",
  siret: "909 121 279 00014"
};

// Available variables
const VARIABLES = [
  { key: 'prenom', label: 'Prénom du client', example: 'Jean' },
  { key: 'nom', label: 'Nom du client', example: 'Dupont' },
  { key: 'email', label: 'Email du client', example: 'jean@email.com' },
  { key: 'date_evenement', label: 'Date de l\'événement', example: '15 Janvier 2025' },
  { key: 'heure_evenement', label: 'Heure de l\'événement', example: '18h00' },
  { key: 'lieu_evenement', label: 'Lieu de l\'événement', example: 'Salle des Fêtes, Istres' },
  { key: 'formule', label: 'Formule choisie', example: 'PhotoBooth Premium' },
  { key: 'prix_total', label: 'Prix total', example: '590' },
  { key: 'montant_acompte', label: 'Montant de l\'acompte', example: '177' },
  { key: 'solde_restant', label: 'Solde restant', example: '413' },
  { key: 'lien_contrat', label: 'Lien vers le contrat', example: 'https://...' },
  { key: 'lien_paiement', label: 'Lien de paiement', example: 'https://...' },
];

// Toolbar Button Component
const ToolbarButton = ({ onClick, active, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-2 rounded hover:bg-slate-200 transition-colors ${active ? 'bg-slate-200 text-amber-600' : 'text-slate-600'}`}
  >
    {children}
  </button>
);

// TipTap Editor Component
const RichTextEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL du lien:', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-slate-50 border-b">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Gras"
        >
          <FiBold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italique"
        >
          <FiItalic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Souligné"
        >
          <FiUnderline className="h-4 w-4" />
        </ToolbarButton>
        
        <div className="w-px bg-slate-300 mx-1" />
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Titre"
        >
          <span className="font-bold text-sm">H2</span>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Sous-titre"
        >
          <span className="font-bold text-sm">H3</span>
        </ToolbarButton>
        
        <div className="w-px bg-slate-300 mx-1" />
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Liste à puces"
        >
          <FiList className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Liste numérotée"
        >
          <span className="text-sm font-medium">1.</span>
        </ToolbarButton>
        
        <div className="w-px bg-slate-300 mx-1" />
        
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          title="Aligner à gauche"
        >
          <FiAlignLeft className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          title="Centrer"
        >
          <FiAlignCenter className="h-4 w-4" />
        </ToolbarButton>
        
        <div className="w-px bg-slate-300 mx-1" />
        
        <ToolbarButton
          onClick={setLink}
          active={editor.isActive('link')}
          title="Ajouter un lien"
        >
          <FiLink className="h-4 w-4" />
        </ToolbarButton>
      </div>
      
      {/* Editor */}
      <EditorContent 
        editor={editor} 
        className="prose prose-sm max-w-none p-4 min-h-[200px] focus:outline-none"
      />
    </div>
  );
};

// Email Header Component
const EmailHeader = () => (
  <div style={{
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    padding: '30px 20px',
    textAlign: 'center',
    borderRadius: '12px 12px 0 0'
  }}>
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <img 
        src={COMPANY_INFO.logo} 
        alt={COMPANY_INFO.name}
        style={{ height: '80px', display: 'block', margin: '0 auto 10px auto' }}
      />
    </div>
    <p style={{ 
      color: '#d4af37', 
      margin: '0',
      fontSize: '12px',
      letterSpacing: '2px'
    }}>
      PHOTOBOOTH • VIDEOBOOTH 360 • LIVRE D'OR VIDÉO
    </p>
  </div>
);

// Email Footer Component
const EmailFooter = () => (
  <div style={{
    background: '#f8f9fa',
    padding: '25px 20px',
    borderTop: '3px solid #d4af37',
    borderRadius: '0 0 12px 12px'
  }}>
    <div style={{ textAlign: 'center', marginBottom: '15px' }}>
      <p style={{ margin: '0 0 5px 0', fontWeight: '600', color: '#1a1a2e', fontSize: '14px' }}>
        {COMPANY_INFO.name}
      </p>
      <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>
        📍 {COMPANY_INFO.address}
      </p>
      <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
        📞 {COMPANY_INFO.phone} | ✉️ {COMPANY_INFO.email}
      </p>
      <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
        🌐 {COMPANY_INFO.website}
      </p>
    </div>
    
    <div style={{ 
      borderTop: '1px solid #ddd', 
      paddingTop: '12px',
      marginTop: '12px'
    }}>
      <p style={{ 
        margin: '0', 
        fontSize: '10px', 
        color: '#999',
        textAlign: 'center',
        lineHeight: '1.4'
      }}>
        Ce message est confidentiel et destiné exclusivement à son destinataire. 
        Si vous l'avez reçu par erreur, merci de le supprimer et d'en informer l'expéditeur.
      </p>
    </div>
    
    <div style={{ textAlign: 'center', marginTop: '10px' }}>
      <p style={{ margin: '0', fontSize: '9px', color: '#bbb' }}>
        SIRET: {COMPANY_INFO.siret} | TVA non applicable, art. 293 B du CGI
      </p>
    </div>
  </div>
);

// Generate full HTML email with header/footer
const generateFullEmailHtml = (bodyContent, subject) => {
  // Transform button placeholders to real buttons
  let processedContent = bodyContent;
  const buttonRegex = /\{\{BOUTON:([^:]+):([^}]+)\}\}/g;
  processedContent = processedContent.replace(buttonRegex, (match, label, varName) => {
    return `<table role="presentation" cellspacing="0" cellpadding="0" style="margin: 20px auto;">
      <tr>
        <td style="background: linear-gradient(135deg, #d4af37, #b8960c); border-radius: 8px;">
          <a href="{{${varName}}}" style="display: inline-block; padding: 15px 35px; color: #ffffff; text-decoration: none; font-weight: bold; font-size: 16px;">📝 ${label}</a>
        </td>
      </tr>
    </table>`;
  });

  return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${subject}</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: Arial, sans-serif;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f4f4f4;">
        <tr>
            <td align="center" style="padding: 40px 20px;">
                <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="background-color: #ffffff; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.1);">
                    <tr>
                        <td style="background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); padding: 30px 20px; text-align: center; border-radius: 12px 12px 0 0;">
                            <img src="${COMPANY_INFO.logo}" alt="${COMPANY_INFO.name}" style="height: 80px; margin-bottom: 10px;">
                            <p style="color: #d4af37; margin: 0; font-size: 12px; letter-spacing: 2px;">
                                PHOTOBOOTH • VIDEOBOOTH 360 • LIVRE D'OR VIDÉO
                            </p>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 40px 30px; font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6; color: #333;">
                            ${processedContent}
                        </td>
                    </tr>
                    <tr>
                        <td style="background: #f8f9fa; padding: 25px 20px; border-top: 3px solid #d4af37; border-radius: 0 0 12px 12px;">
                            <p style="margin: 0 0 5px 0; font-weight: 600; color: #1a1a2e; text-align: center; font-size: 14px;">${COMPANY_INFO.name}</p>
                            <p style="margin: 0; font-size: 12px; color: #666; text-align: center;">📍 ${COMPANY_INFO.address}</p>
                            <p style="margin: 5px 0; font-size: 12px; color: #666; text-align: center;">📞 ${COMPANY_INFO.phone} | ✉️ ${COMPANY_INFO.email}</p>
                            <p style="margin: 5px 0; font-size: 12px; color: #666; text-align: center;">🌐 ${COMPANY_INFO.website}</p>
                            <hr style="border: none; border-top: 1px solid #ddd; margin: 15px 0;">
                            <p style="margin: 0; font-size: 10px; color: #999; text-align: center; line-height: 1.4;">
                                Ce message est confidentiel et destiné exclusivement à son destinataire. Si vous l'avez reçu par erreur, merci de le supprimer.
                            </p>
                            <p style="margin: 10px 0 0 0; font-size: 9px; color: #bbb; text-align: center;">
                                SIRET: ${COMPANY_INFO.siret} | TVA non applicable, art. 293 B du CGI
                            </p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;
};

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bodyContent, setBodyContent] = useState('');
  const [showVariables, setShowVariables] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/email/system-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate({ ...template });
    setBodyContent(template.body_content || '');
    setEditDialogOpen(true);
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
    setPreviewDialogOpen(true);
  };

  const insertVariable = (variable) => {
    setBodyContent(prev => prev + `<span style="background-color: #fef3c7; padding: 2px 6px; border-radius: 4px;">{{${variable}}}</span>&nbsp;`);
    toast.success(`Variable {{${variable}}} insérée`);
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const fullHtml = generateFullEmailHtml(bodyContent, selectedTemplate.subject);
      
      const response = await fetch(`${API_URL}/api/email/system-templates/${selectedTemplate.key}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: selectedTemplate.name,
          subject: selectedTemplate.subject,
          html_content: fullHtml,
          body_content: bodyContent
        })
      });
      
      if (response.ok) {
        toast.success('Template sauvegardé');
        setEditDialogOpen(false);
        loadTemplates();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (templateKey) => {
    if (!window.confirm('Réinitialiser ce template aux valeurs par défaut ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/email/system-templates/${templateKey}/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Template réinitialisé');
        loadTemplates();
        setEditDialogOpen(false);
      }
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const getTemplateIcon = (key) => {
    const icons = {
      'reservation_accepted': '✅',
      'contract_signed': '📝',
      'deposit_paid': '💰',
      'password_reset': '🔐',
      'account_created': '👋',
      'reminder_event': '📅'
    };
    return icons[key] || '📧';
  };

  const renderPreviewEmail = (template) => {
    let content = template.body_content || '';
    
    // Replace button placeholders with preview buttons FIRST
    const buttonRegex = /\{\{BOUTON:([^:]+):([^}]+)\}\}/g;
    content = content.replace(buttonRegex, (match, label, varName) => {
      return `<div style="text-align: center; margin: 20px 0;">
        <span style="display: inline-block; background: linear-gradient(135deg, #d4af37, #b8960c); padding: 15px 35px; border-radius: 8px; color: white; font-weight: bold;">📝 ${label}</span>
      </div>`;
    });
    
    // Then replace regular variables
    VARIABLES.forEach(v => {
      const regex = new RegExp(`\\{\\{${v.key}\\}\\}`, 'g');
      content = content.replace(regex, `<span style="background-color: #d4edda; padding: 2px 6px; border-radius: 4px; font-weight: 500;">${v.example}</span>`);
    });
    
    return content;
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Chargement des templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FiMail className="text-amber-500" />
            Modèles d'Emails
          </h1>
          <p className="text-slate-500 mt-1">
            Personnalisez les emails envoyés automatiquement à vos clients
          </p>
        </div>
        <Button variant="outline" onClick={loadTemplates}>
          <FiRefreshCw className="mr-2 h-4 w-4" /> Actualiser
        </Button>
      </div>

      {/* Info Box */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">En-tête et pied de page automatiques</p>
              <p className="mt-1">
                Tous vos emails incluent automatiquement le logo Just A Smile Events en en-tête 
                et vos coordonnées + mention de confidentialité en pied de page.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {templates.map((template) => (
          <motion.div
            key={template.key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="hover:shadow-lg transition-shadow h-full">
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">{getTemplateIcon(template.key)}</span>
                  <div className="flex-1">
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">{template.description}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Objet :</p>
                    <p className="text-sm font-medium text-slate-700">{template.subject}</p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handlePreview(template)}
                    >
                      <FiEye className="mr-1 h-3 w-3" /> Aperçu
                    </Button>
                    <Button 
                      size="sm" 
                      className="flex-1 bg-amber-500 hover:bg-amber-600"
                      onClick={() => handleEdit(template)}
                    >
                      <FiEdit2 className="mr-1 h-3 w-3" /> Modifier
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{selectedTemplate && getTemplateIcon(selectedTemplate.key)}</span>
              Modifier : {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {/* Subject */}
              <div>
                <Label>Objet de l'email</Label>
                <Input
                  value={selectedTemplate.subject || ''}
                  onChange={(e) => setSelectedTemplate({ ...selectedTemplate, subject: e.target.value })}
                  placeholder="Ex: Votre réservation a été confirmée"
                />
              </div>

              {/* Variables dropdown */}
              <div>
                <button
                  onClick={() => setShowVariables(!showVariables)}
                  className="flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700"
                >
                  <FiType className="h-4 w-4" />
                  Insérer une variable personnalisée
                  <FiChevronDown className={`h-4 w-4 transition-transform ${showVariables ? 'rotate-180' : ''}`} />
                </button>
                
                {showVariables && (
                  <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-700 mb-2">
                      Cliquez sur une variable pour l'insérer :
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {VARIABLES.map((v) => (
                        <button
                          key={v.key}
                          onClick={() => insertVariable(v.key)}
                          className="px-3 py-1.5 bg-white border border-amber-300 rounded-full text-xs hover:bg-amber-100 transition-colors"
                          title={v.label}
                        >
                          {`{{${v.key}}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Email Preview Structure */}
              <div className="border rounded-xl overflow-hidden shadow-sm">
                {/* Header Preview */}
                <EmailHeader />
                
                {/* Body Editor */}
                <div className="bg-white p-4">
                  <Label className="mb-2 block">Contenu de l'email</Label>
                  <RichTextEditor 
                    content={bodyContent} 
                    onChange={setBodyContent}
                  />
                </div>
                
                {/* Footer Preview */}
                <EmailFooter />
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between items-center border-t pt-4">
            <Button 
              variant="outline" 
              onClick={() => handleReset(selectedTemplate?.key)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <FiRefreshCw className="mr-2 h-4 w-4" /> Réinitialiser
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600"
              >
                {saving ? 'Sauvegarde...' : (
                  <><FiSave className="mr-2 h-4 w-4" /> Sauvegarder</>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto p-0">
          <div className="sticky top-0 bg-white border-b p-4 z-10">
            <DialogTitle className="flex items-center gap-2">
              <FiEye className="text-amber-500" />
              Aperçu : {selectedTemplate?.name}
            </DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              <strong>Objet :</strong> {selectedTemplate?.subject}
            </p>
          </div>
          
          {selectedTemplate && (
            <div className="p-6 bg-slate-100">
              <div className="max-w-[600px] mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
                <EmailHeader />
                <div 
                  className="p-8"
                  style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.6', color: '#333', fontSize: '14px' }}
                  dangerouslySetInnerHTML={{ __html: renderPreviewEmail(selectedTemplate) }}
                />
                <EmailFooter />
              </div>
            </div>
          )}

          <div className="sticky bottom-0 bg-white border-t p-4">
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)} className="w-full">
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom styles for TipTap */}
      <style>{`
        .ProseMirror {
          min-height: 200px;
          padding: 12px;
          outline: none;
        }
        .ProseMirror p {
          margin: 0 0 10px 0;
        }
        .ProseMirror h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0 0 15px 0;
          color: #d4af37;
        }
        .ProseMirror h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 0 0 10px 0;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 20px;
          margin: 10px 0;
        }
        .ProseMirror a {
          color: #d4af37;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}
